<script setup lang="ts">
import { computed } from 'vue'

import { useCall } from '../../composables/useCall'
import { useCallStore } from '../../stores/call'

const callStore = useCallStore()
const { acceptIncomingCall, rejectIncomingCall } = useCall()

const show = computed(() => !!callStore.incomingCallOffer)

const callTypeText = computed(() => {
  if (!callStore.incomingCallOffer)
    return ''
  return callStore.incomingCallOffer.type === 'video' ? '视频通话' : '语音通话'
})

const characterName = computed(() => {
  return callStore.incomingCallOffer?.characterName || '未知'
})

const characterAvatar = computed(() => {
  // 统一使用来电时传入的头像（联系人自定义头像）
  return callStore.incomingCallOffer?.characterAvatar || '/BOT.jpg'
})

async function handleAccept() {
  // 传入头像，确保通话界面能正确显示
  await acceptIncomingCall(characterAvatar.value)
}

async function handleReject() {
  await rejectIncomingCall()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="show"
        fixed inset-0 z-300
        class="incoming-call-backdrop"
        flex="~ col" items-center justify-between
        p-8 text-white
      >
        <!-- 来电信息 - 上部 -->
        <div mt-16 text-center>
          <!-- 头像容器 - 带动画光环 -->
          <div relative inline-block>
            <img
              :src="characterAvatar"
              :alt="characterName"
              class="incoming-avatar"
              h-32 w-32 rounded-full object-cover
              border="3 white/40"
            >
            <!-- 多重呼吸光环 -->
            <div
              absolute inset-0
              class="ring-animation ring-1"
              rounded-full
              border="3 green-400"
            />
            <div
              absolute inset-0
              class="ring-animation ring-2"
              rounded-full
              border="3 green-300"
            />
          </div>

          <!-- 来电提示文字 -->
          <div mt-8 flex="~ col" items-center gap-2>
            <p text-sm text-white tracking-wider uppercase op-60>
              来电邀请
            </p>
            <p text-4xl tracking-wide>
              {{ characterName }}
            </p>
            <div mt-2 flex items-center gap-2>
              <div
                :class="callTypeText === '视频通话' ? 'i-solar:videocamera-bold-duotone' : 'i-solar:phone-bold-duotone'"
                text-xl text-green-400
              />
              <p text-lg text-white op-80>
                {{ callTypeText }}
              </p>
            </div>
          </div>

          <!-- 呼吸提示 -->
          <div mt-6 flex items-center justify-center gap-2>
            <div class="calling-dot" />
            <div class="calling-dot" />
            <div class="calling-dot" />
          </div>
        </div>

        <!-- 占位，使按钮在底部 -->
        <div />

        <!-- 操作按钮 - 底部 -->
        <div max-w-md w-full flex items-center justify-around px-8>
          <!-- 拒接按钮 -->
          <div text-center>
            <button
              class="reject-button"
              h-20 w-20 flex items-center justify-center rounded-full
              transition="all duration-300"
              @click="handleReject"
            >
              <div i-carbon:phone-off h-8 w-8 />
            </button>
            <p mt-3 text-sm tracking-wide>
              拒接
            </p>
          </div>

          <!-- 接听按钮 -->
          <div text-center>
            <button
              class="accept-button"
              h-20 w-20 flex items-center justify-center rounded-full
              transition="all duration-300"
              @click="handleAccept"
            >
              <div i-carbon:phone h-8 w-8 />
            </button>
            <p mt-3 text-sm tracking-wide>
              接听
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* 来电背景 */
.incoming-call-backdrop {
 background: linear-gradient(135deg,
 #064e3b 0%,
 #047857 50%,
 #065f46 100%
 );
 animation: incomingGradient 8s ease infinite;
 background-size: 200% 200%;
}

@keyframes incomingGradient {
 0%, 100% {
 background-position: 0% 50%;
 }
 50% {
 background-position: 100% 50%;
 }
}

/* 来电头像 */
.incoming-avatar {
 box-shadow: 0 12px 40px rgba(16, 185, 129, 0.4),
 0 0 0 4px rgba(255, 255, 255, 0.2);
 animation: avatarBounce 2s ease-in-out infinite;
}

@keyframes avatarBounce {
 0%, 100% {
 transform: translateY(0) scale(1);
 }
 50% {
 transform: translateY(-10px) scale(1.02);
 }
}

/* 光环动画 */
.ring-animation {
 pointer-events: none;
 opacity: 0;
}

.ring-1 {
 animation: ringPulse 2s ease-out infinite;
 animation-delay: 0s;
}

.ring-2 {
 animation: ringPulse 2s ease-out infinite;
 animation-delay: 0.5s;
}

@keyframes ringPulse {
 0% {
 transform: scale(1);
 opacity: 1;
 }
 100% {
 transform: scale(2.5);
 opacity: 0;
 }
}

/* 呼叫点动画 */
.calling-dot {
 width: 10px;
 height: 10px;
 background: #10b981;
 border-radius: 50%;
 box-shadow: 0 0 15px rgba(16, 185, 129, 0.8);
 animation: dotBounce 1.4s ease-in-out infinite;
}

.calling-dot:nth-child(1) {
 animation-delay: 0s;
}

.calling-dot:nth-child(2) {
 animation-delay: 0.2s;
}

.calling-dot:nth-child(3) {
 animation-delay: 0.4s;
}

@keyframes dotBounce {
 0%, 60%, 100% {
 transform: scale(1);
 opacity: 1;
 }
 30% {
 transform: scale(1.5);
 opacity: 0.7;
 }
}

/* 拒接按钮 */
.reject-button {
 background: linear-gradient(135deg, #ef4444, #dc2626);
 border: 2px solid rgba(255, 255, 255, 0.3);
 box-shadow: 0 8px 24px rgba(239, 68, 68, 0.5);
}

.reject-button:hover {
 background: linear-gradient(135deg, #dc2626, #b91c1c);
 transform: scale(1.1);
 box-shadow: 0 12px 32px rgba(239, 68, 68, 0.7);
}

.reject-button:active {
 transform: scale(0.95);
}

/* 接听按钮 */
.accept-button {
 background: linear-gradient(135deg, #10b981, #059669);
 border: 2px solid rgba(255, 255, 255, 0.3);
 box-shadow: 0 8px 24px rgba(16, 185, 129, 0.5);
 animation: acceptButtonPulse 2s ease-in-out infinite;
}

.accept-button:hover {
 background: linear-gradient(135deg, #059669, #047857);
 transform: scale(1.1);
 box-shadow: 0 12px 32px rgba(16, 185, 129, 0.7);
 animation: none;
}

.accept-button:active {
 transform: scale(0.95);
}

@keyframes acceptButtonPulse {
 0%, 100% {
 box-shadow: 0 8px 24px rgba(16, 185, 129, 0.5);
 }
 50% {
 box-shadow: 0 8px 32px rgba(16, 185, 129, 0.8);
 }
}

/* 过渡动画 */
.dialog-enter-active,
.dialog-leave-active {
 transition: opacity 0.4s ease;
}

/* 离开过渡期间立即禁用指针事件，防止遮挡下层 CallModal 的交互 */
.dialog-leave-active {
 pointer-events: none !important;
}

.dialog-enter-from,
.dialog-leave-to {
 opacity: 0;
}

.dialog-enter-active > div,
.dialog-leave-active > div {
 transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dialog-enter-from > div:first-child {
 transform: scale(0.7) translateY(50px);
 opacity: 0;
}

.dialog-leave-to > div:first-child {
 transform: scale(0.7) translateY(-50px);
 opacity: 0;
}
</style>
